import { useEffect, useState, type ReactElement } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Checkbox,
    CloseButton,
    Group,
    Menu,
    Paper,
    Progress,
    SegmentedControl,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import ResponsiveRow from '@/shared/responsive/ResponsiveRow';
import { useDebouncedValue } from '@mantine/hooks';
import {
    ChevronDown,
    ChevronsDownUp,
    ChevronsUpDown,
    FileText,
    List,
    Network,
    Plus,
    RotateCcw,
    Save,
    Search,
    Sparkles,
} from 'lucide-react';
import type { CategoriesView, TaxonomyStats } from './UsageCategoryTaxonomyTypes';

interface TaxonomyToolbarProps {
    stats: TaxonomyStats;
    isDirty: boolean;
    saving: boolean;
    pendingChangeCount: number;
    filterText: string;
    isFiltering: boolean;
    matchCount: number;
    view: CategoriesView;
    includeGeneral: boolean;
    canGenerate: boolean;
    isGenerating: boolean;
    generationPhase: 'idle' | 'building' | 'generating';
    generationProgress: { done: number; total: number } | null;
    onFilterChange: (value: string) => void;
    onViewChange: (view: CategoriesView) => void;
    onToggleGeneral: (value: boolean) => void;
    onGenerateFromDirectory: () => void;
    onUseDefault: () => void;
    onAddCategory: () => void;
    onExpandAll: () => void;
    onCollapseAll: () => void;
    onRevert: () => void;
    onSave: () => void;
}

/** Sticky top toolbar: generation menu, add/expand controls, filter + view
 * toggle, and Save/Revert — everything for the taxonomy in one bar. */
export default function TaxonomyToolbar(props: TaxonomyToolbarProps): ReactElement {
    const [filter, setFilter] = useState(props.filterText);
    const [debouncedFilter] = useDebouncedValue(filter, 200);
    const { onFilterChange } = props;

    useEffect(() => {
        if (debouncedFilter !== props.filterText) onFilterChange(debouncedFilter);
    }, [debouncedFilter, props.filterText, onFilterChange]);

    const clearFilter = (): void => {
        setFilter('');
        onFilterChange('');
    };

    const summary = props.isFiltering
        ? `${props.matchCount} ${props.matchCount === 1 ? 'match' : 'matches'}`
        : `${props.stats.categories} categories · ${props.stats.subcategories} subcategories${props.stats.disabled > 0 ? ` · ${props.stats.disabled} disabled` : ''}`;

    const generateLabel =
        props.generationPhase === 'building'
            ? 'Building groups…'
            : props.generationPhase === 'generating' && props.generationProgress
              ? `Generating ${props.generationProgress.done} of ${props.generationProgress.total}…`
              : 'Generate';

    return (
        <Paper withBorder p="sm" style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--bm-color-body)' }}>
            <Stack gap="sm">
                <ResponsiveRow>
                    <Group gap={6} wrap="nowrap">
                        <Menu position="bottom-start" withinPortal shadow="md">
                            <Menu.Target>
                                <Button
                                    size="xs"
                                    variant="light"
                                    leftSection={<Sparkles size={15} />}
                                    rightSection={<ChevronDown size={14} />}
                                    loading={props.isGenerating}
                                    disabled={!props.canGenerate}
                                >
                                    {generateLabel}
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Label>Build categories</Menu.Label>
                                <Menu.Item
                                    closeMenuOnClick={false}
                                    leftSection={
                                        <Checkbox
                                            size="xs"
                                            checked={props.includeGeneral}
                                            readOnly
                                            tabIndex={-1}
                                            aria-label="Include a General category"
                                        />
                                    }
                                    onClick={() => props.onToggleGeneral(!props.includeGeneral)}
                                >
                                    Include a General category
                                </Menu.Item>
                                <Menu.Divider />
                                <Menu.Item leftSection={<Sparkles size={14} />} onClick={props.onGenerateFromDirectory}>
                                    From your identity provider
                                </Menu.Item>
                                <Menu.Item leftSection={<FileText size={14} />} onClick={props.onUseDefault}>
                                    Use a default set
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                        <Button size="xs" variant="default" leftSection={<Plus size={15} />} onClick={props.onAddCategory}>
                            Category
                        </Button>
                        <Tooltip label="Expand all" openDelay={400}>
                            <ActionIcon size="lg" variant="subtle" color="gray" aria-label="Expand all" onClick={props.onExpandAll}>
                                <ChevronsUpDown size={16} />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Collapse all" openDelay={400}>
                            <ActionIcon size="lg" variant="subtle" color="gray" aria-label="Collapse all" onClick={props.onCollapseAll}>
                                <ChevronsDownUp size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                    <Group gap="xs" wrap="nowrap" justify="flex-end">
                        {props.pendingChangeCount > 0 && (
                            <Badge color="yellow" variant="light">
                                {props.pendingChangeCount} unsaved
                            </Badge>
                        )}
                        <Button size="xs" variant="default" leftSection={<RotateCcw size={15} />} disabled={!props.isDirty || props.saving} onClick={props.onRevert}>
                            Revert
                        </Button>
                        <Button size="xs" leftSection={<Save size={15} />} loading={props.saving} disabled={!props.isDirty} onClick={props.onSave}>
                            Save
                        </Button>
                    </Group>
                </ResponsiveRow>

                <ResponsiveRow>
                    <Group gap="sm" wrap="wrap" style={{ minWidth: 0 }}>
                        <TextInput
                            size="xs"
                            placeholder="Filter categories"
                            leftSection={<Search size={14} />}
                            value={filter}
                            onChange={(e) => setFilter(e.currentTarget.value)}
                            rightSection={filter.length > 0 ? <CloseButton size="sm" onClick={clearFilter} aria-label="Clear filter" /> : null}
                            w={{ base: '100%', sm: 240 }}
                        />
                        <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                            {summary}
                        </Text>
                    </Group>
                    <SegmentedControl
                        size="xs"
                        value={props.view}
                        onChange={(value) => props.onViewChange(value as CategoriesView)}
                        data={[
                            { value: 'list', label: (<Group gap={6} wrap="nowrap"><List size={14} /> List</Group>) },
                            { value: 'chart', label: (<Group gap={6} wrap="nowrap"><Network size={14} /> Chart</Group>) },
                        ]}
                    />
                </ResponsiveRow>

                {props.isGenerating && (
                    <Stack gap={4}>
                        <Text size="xs" c="dimmed">
                            {props.generationPhase === 'building'
                                ? 'Building groups from your directory…'
                                : props.generationProgress
                                  ? `Generating task groups — ${props.generationProgress.done} of ${props.generationProgress.total}…`
                                  : 'Working…'}
                        </Text>
                        <Progress
                            size="sm"
                            striped
                            animated
                            value={
                                props.generationPhase === 'generating' && props.generationProgress && props.generationProgress.total > 0
                                    ? (props.generationProgress.done / props.generationProgress.total) * 100
                                    : 100
                            }
                        />
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
}
